/**
 * feat-302 — View as: the pickers (toolbar slot), the banner (under the
 * toolbar), the TOC line, and the empty-result card (design NOTES §1, §2, §5).
 */
import React from 'react';
import { ChevronDown, CircleAlert, Eye, Loader2, X } from 'lucide-react';
import { AudienceOptions } from '../../../../services/quizContentAdminService';
import { ERRBTN, FOCUS } from '../InlineEditable';
import { AUDIENCE_COPY as COPY } from './audienceCopy';
import { ViewAsState } from './useViewAs';

// --- class constants, verbatim from design NOTES §3 -------------------------------
export const VA_SELECT = `appearance-none h-8 max-w-[11rem] truncate pl-3 pr-8 rounded-full bg-white text-sm font-medium text-ink ring-1 ring-black/10 hover:bg-cream-dark cursor-pointer ${FOCUS}`;
export const VA_SELECT_SET = `appearance-none h-8 max-w-[11rem] truncate pl-3 pr-8 rounded-full bg-white text-sm font-semibold text-ink ring-2 ring-ink/50 shadow-sm hover:bg-cream-dark cursor-pointer ${FOCUS}`;
const VA_BANNER =
  'mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-xl bg-white ring-1 ring-black/10 shadow-sm px-3 py-2 text-sm';
const VA_BADGE =
  'inline-flex items-center gap-1.5 rounded-full bg-ink text-white px-2.5 h-6 text-xs font-bold';
const VA_CLEAR = `inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-sm font-semibold text-ink ring-1 ring-black/10 bg-white hover:bg-cream-dark ${FOCUS}`;
const VA_TOC = 'mx-2 mt-2 flex items-center gap-2 rounded-lg bg-cream-dark px-2.5 py-1.5 text-xs text-ink';
const ERRBOX =
  'rounded-lg bg-danger-surface border border-danger-line/40 px-3 py-2 text-xs leading-snug text-danger-text';

const Picker: React.FC<{
  aria: string;
  any: string;
  value: string | null;
  options: Array<{ value: string; label: string }>;
  onChange: (v: string | null) => void;
}> = ({ aria, any, value, options, onChange }) => {
  // A URL value the list does not have still shows as itself, never as "Any".
  const known = value === null || options.some((o) => o.value === value);
  return (
    <span className="relative inline-flex">
      <select
        aria-label={aria}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        className={value ? VA_SELECT_SET : VA_SELECT}
      >
        <option value="">{any}</option>
        {!known && value && <option value={value}>{value}</option>}
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown
        className="pointer-events-none absolute right-2.5 top-2 w-4 h-4 text-inkMuted"
        aria-hidden
      />
    </span>
  );
};

/** The toolbar slot's content. Native `<select>`s so phones get the OS picker. */
export const ViewAsControls: React.FC<{ options: AudienceOptions; viewAs: ViewAsState }> = ({
  options,
  viewAs,
}) => (
  <>
    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-inkMuted">
      <Eye className="w-4 h-4" aria-hidden />
      {COPY.viewAs}
    </span>
    <Picker
      aria={COPY.viewAsLocationAria}
      any={COPY.anyLocation}
      value={viewAs.locationId}
      options={options.locations.map((l) => ({ value: l.id, label: l.name }))}
      onChange={viewAs.setLocation}
    />
    {options.roles.length > 0 && (
      <Picker
        aria={COPY.viewAsRoleAria}
        any={COPY.anyRole}
        value={viewAs.roleKey}
        options={options.roles.map((r) => ({ value: r.role_key, label: r.display_name }))}
        onChange={viewAs.setRole}
      />
    )}
  </>
);

/** Under the toolbar, inside `<header>` (NOTES §2). */
export const ViewAsBanner: React.FC<{
  viewAs: ViewAsState;
  counts: { t: number; tt: number; l: number; ll: number } | null;
}> = ({ viewAs, counts }) => {
  if (!viewAs.active || !viewAs.viewName) return null;
  if (viewAs.status === 'error') {
    return (
      <div
        className={`mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 ${ERRBOX} !text-sm`}
        role="alert"
        data-testid="view-as-banner"
      >
        <CircleAlert className="w-4 h-4 shrink-0" aria-hidden />
        <span>
          <b>{COPY.bannerError(viewAs.viewName)}</b> {COPY.bannerErrorTail}
        </span>
        <button type="button" className={ERRBTN} onClick={viewAs.retry}>
          {COPY.tryAgain}
        </button>
        <button type="button" className={ERRBTN} onClick={viewAs.clear}>
          {COPY.clear}
        </button>
      </div>
    );
  }
  return (
    <div className={VA_BANNER} data-testid="view-as-banner">
      <span className={VA_BADGE}>
        <Eye className="w-3.5 h-3.5" aria-hidden />
        {COPY.viewingAs}
      </span>
      <span className="font-semibold text-ink">{viewAs.viewName}</span>
      {viewAs.status === 'loading' || !counts ? (
        <span className="inline-flex items-center gap-1.5 text-inkMuted" role="status">
          <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden />
          {COPY.updating}
        </span>
      ) : (
        <span className="text-inkMuted">{COPY.bannerCounts(counts.t, counts.tt, counts.l, counts.ll)}</span>
      )}
      <button type="button" className={`${VA_CLEAR} ml-auto`} onClick={viewAs.clear}>
        <X className="w-3.5 h-3.5" aria-hidden />
        {COPY.clear}
      </button>
      <p className="basis-full text-xs text-inkMuted">{COPY.bannerSubline}</p>
    </div>
  );
};

/** The line at the top of the TOC (docked panel, popover and sheet). */
export const ViewAsTocLine: React.FC<{ viewAs: ViewAsState; empty: boolean }> = ({ viewAs, empty }) => {
  if (!viewAs.active || !viewAs.viewName) return null;
  return (
    <>
      <div className={VA_TOC} data-testid="view-as-toc-line">
        <Eye className="w-3.5 h-3.5 text-inkMuted shrink-0" aria-hidden />
        <span className="min-w-0 flex-1 truncate">{COPY.tocLine(viewAs.viewName)}</span>
        <button type="button" className={ERRBTN} onClick={viewAs.clear}>
          {COPY.clear}
        </button>
      </div>
      {empty && <p className="mx-4 mt-2 text-xs text-inkMuted italic">{COPY.tocEmpty}</p>}
    </>
  );
};

/** The empty-result card in `<main>` (NOTES §1, F7). */
export const ViewAsEmpty: React.FC<{ viewAs: ViewAsState }> = ({ viewAs }) => (
  <div className="rounded-2xl bg-white ring-1 ring-black/5 px-6 py-10 text-center" data-testid="view-as-empty">
    <div className="mx-auto w-10 h-10 rounded-full bg-cream-dark grid place-items-center text-inkMuted">
      <Eye className="w-5 h-5" aria-hidden />
    </div>
    <h2 className="mt-3 font-display text-xl font-semibold text-ink">
      {COPY.emptyTitle(viewAs.viewName ?? '')}
    </h2>
    <p className="mt-1 text-sm text-inkMuted max-w-md mx-auto">
      {COPY.emptyBody(viewAs.locationName, viewAs.roleName)}
    </p>
    <button type="button" className={`${VA_CLEAR} mt-4`} onClick={viewAs.clear}>
      <X className="w-3.5 h-3.5" aria-hidden />
      {COPY.clearViewAs}
    </button>
  </div>
);
